<template>
  <div>
    <v-card class="manageAsList col-12 mt-0 mb-4 pb-0">
      <div class="row justify-end mt-0 mb-0 pr-3">
        <div class="row justify-start mt-0 mb-0" style="padding-left: 25px;">
          <v-btn small color="grey darken-4" class="white--text" @click="confirmDelete">내역 삭제</v-btn>
          <v-btn
            v-for="item in statusButtons"
            :key="item.value"
            small
            color="blue"
            class="white--text ml-2"
            @click="confirmStatusChange(item)"
          >
            {{ item.text }}
          </v-btn>
        </div>

        <v-btn small color="grey darken-1" class="white--text mr-3" @click.prevent="openSymptomSetting">
          상품별 고장 증상 설정
        </v-btn>
        <v-btn small color="teal" class="white--text mr-3" @click.prevent="openTrackingUpload">
          운송장번호 엑셀 업로드
        </v-btn>
        <v-btn small color="green" class="white--text" @click.prevent="excelDownload">엑셀 다운로드</v-btn>
      </div>

      <v-data-table
        v-model="selectedAsList"
        :headers="headers"
        :items="asList"
        :options.sync="options"
        :loading="tableLoading"
        :server-items-length="asListCount"
        :items-per-page="$parent.$refs.searchComponent.searchPayload.limit"
        show-select
        :footer-props="{
          'items-per-page-options': [10, 20, 30, 40, 50],
          'items-per-page-text': '목록 노출 수 선택:',
        }"
        item-key="receipt_no"
        scrollable
      >
        <template v-slot:[`item.receipt_no`]="{ item }">
          <p class="mb-0 mt-3 caption text-center">{{ item.receipt_no || "-" }}</p>
        </template>

        <template v-slot:[`item.received_at`]="{ item }">
          <p class="mb-0 mt-3 caption text-center" v-html="$options.filters.defaultDate(item.received_at)"></p>
        </template>

        <template v-slot:[`item.status`]="{ item }">
          <p class="mb-0 mt-3 caption text-center">{{ statusText(item.status) }}</p>
        </template>

        <template v-slot:[`item.member`]="{ item }">
          <div class="text-start my-3">
            <span
              :class="[item.member && item.member.id ? 'primary--text memberInfoHover' : '', 'caption']"
              @click="item.member && item.member.id && showMemberInfo(item.member.id)"
            >
              <div>ID : {{ (item.member && item.member.id) || "-" }}</div>
              이름 : {{ (item.member && item.member.name) || "-" }}
              <br />
              핸드폰 : {{ (item.member && item.member.phone) || "-" }}
              <br />
              이메일 : {{ (item.member && item.member.email) || "-" }}
              <br />
              <div v-if="item.member && item.member.id">회원등급명 : {{ (item.member && item.member.level) || "-" }}</div>
            </span>
          </div>
        </template>

        <template v-slot:[`item.product`]="{ item }">
          <div class="my-3">
            <p class="mb-0 caption text-start">상품명 : {{ getProductValue(item, "product_name") }}</p>
            <p class="mb-0 caption text-start">품목명 : {{ getProductValue(item, "variant_name") }}</p>
            <p class="mb-0 caption text-start">상품코드 : {{ getProductValue(item, "product_code") }}</p>
            <p class="mb-0 caption text-start">품목코드 : {{ getProductValue(item, "variant_code") }}</p>
            <p class="mb-0 caption text-start">
              정품인증코드 :
              <a @click.prevent="openAuthCodeHistory(item)">{{ getProductValue(item, "auth_code") }}</a>
            </p>
            <p class="mb-0 caption text-start">구매처 : {{ getAuthItemValue(item, "구매처") }}</p>
            <p class="mb-0 caption text-start">구매년도 : {{ getAuthItemValue(item, "구매년도") }}</p>
          </div>
        </template>

        <template v-slot:[`item.request`]="{ item }">
          <div class="my-3">
            <p class="mb-0 caption text-start">증상 : {{ getRequestValue(item, "symptom") }}</p>
            <p class="mb-0 caption text-start">신청 상세 내역 : {{ getRequestValue(item, "detail") }}</p>
            <p class="mb-0 caption text-start">보내는 곳 : {{ getSenderAddress(item) }}</p>
            <p class="mb-0 caption text-start">받는 곳 : {{ getReceiverAddress(item) }}</p>
            <div v-if="hasAttachments(item)">
              <p class="mb-0 caption text-start">첨부파일 :</p>
              <p v-for="(file, index) in item.request.attachments" :key="index" class="mb-0 caption text-start">
                <a @click.prevent="openAttachment(file)">첨부파일 {{ index + 1 }}</a>
              </p>
            </div>
            <div v-else>
              <p class="mb-0 caption text-start">첨부파일 : -</p>
            </div>
          </div>
        </template>

        <template v-slot:[`item.status_dates`]="{ item }">
          <div class="my-3">
            <p class="mb-0 caption text-start">접수 완료 : {{ getStatusDate(item, "receipt_done") }}</p>
            <p class="mb-0 caption text-start">센터 입고 : {{ getStatusDate(item, "center_in") }}</p>
            <p class="mb-0 caption text-start">입금 대기 중 : {{ getStatusDate(item, "payment_waiting") }}</p>
            <p class="mb-0 caption text-start">입금 완료 : {{ getStatusDate(item, "payment_done") }}</p>
            <p class="mb-0 caption text-start">A/S 완료 : {{ getStatusDate(item, "as_done") }}</p>
            <p class="mb-0 caption text-start">출고 완료 : {{ getStatusDate(item, "shipping_done") }}</p>
            <p class="mb-0 caption text-start">처리 완료 : {{ getStatusDate(item, "done") }}</p>
          </div>
        </template>

        <template v-slot:[`item.remark`]="{ item }">
          <div class="my-3">
            <p class="mb-0 caption text-start">수리금액 : {{ formatAmount(getRemarkRaw(item, "repair_amount")) }}</p>
            <p class="mb-0 caption text-start">
              운송장번호 :
              <a v-if="getRemarkRaw(item, 'tracking_no')" @click.prevent="openTracking(getRemarkRaw(item, 'tracking_no'))">
                {{ getRemarkRaw(item, "tracking_no") }}
              </a>
              <span v-else>-</span>
            </p>
            <p class="mb-0 caption text-start">메모 : {{ getRemarkValue(item, "memo") }}</p>
            <v-btn small class="mt-2" @click="openRemarkModal(item)">입력</v-btn>
          </div>
        </template>

        <template slot="no-data">
          <div class="noDataProductInfos">내역이 없습니다.</div>
        </template>
      </v-data-table>

      <AuthCodeClickModal ref="AuthCodeClickModal"></AuthCodeClickModal>
      <AsRemarkModal ref="AsRemarkModal" @saved="setRemark"></AsRemarkModal>
      <AsSymptomSettingModal ref="AsSymptomSettingModal"></AsSymptomSettingModal>
      <AsTrackingExcelUploadModal ref="AsTrackingExcelUploadModal"></AsTrackingExcelUploadModal>
    </v-card>
  </div>
</template>

<script>
import CommonMixin from "@/mixins/CommonMixin.js";
import M from "@/libs/mapping.js";
import Filters from "@/libs/authservice/filters.js";
import Excel from "@/libs/excel.js";
import AuthCodeClickModal from "@/components/authservice/authcode/modal/AuthCodeClickModal.vue";
import AsRemarkModal from "@/components/authservice/as/modal/AsRemarkModal.vue";
import AsSymptomSettingModal from "@/components/authservice/as/modal/AsSymptomSettingModal.vue";
import AsTrackingExcelUploadModal from "@/components/authservice/as/modal/AsTrackingExcelUploadModal.vue";

export default {
  data() {
    return {
      headers: [
        { text: "접수번호", align: "center", sortable: false, value: "receipt_no", width: 100 },
        { text: "접수일시", align: "center", sortable: false, value: "received_at", width: 120 },
        { text: "A/S 상태", align: "center", sortable: false, value: "status", width: 110 },
        { text: "A/S 신청 회원", align: "center", sortable: false, value: "member" },
        { text: "A/S 신청 상품", align: "center", sortable: false, value: "product" },
        { text: "A/S 신청 내역", align: "center", sortable: false, value: "request" },
        { text: "A/S 상태별 처리 일자", align: "center", sortable: false, value: "status_dates", width: 180 },
        { text: "비고", align: "center", sortable: false, value: "remark", width: 200 },
      ],
      asList: [],
      asListCount: 0,
      selectedAsList: [],
      options: {},
      tableLoading: false,
      excelData: {
        Excel: null,
        totalLimit: 200000,
        limit: 10000,
        totalCount: 0,
        offset: 0,
        sheetData: [],
        sheetHeader: {
          receipt_no: "A/S 접수번호",
          received_at: "A/S 접수일시",
          status: "A/S 상태",
          member_id: "회원 ID",
          member_name: "이름",
          member_phone: "휴대폰",
          member_email: "이메일",
          member_level: "회원등급명",
          sender_zip: "보내는 곳 우편번호",
          sender_addr: "보내는 곳 주소",
          sender_addr_detail: "보내는 곳 상세주소",
          receiver_zip: "받는 곳 우편번호",
          receiver_addr: "받는 곳 주소",
          receiver_addr_detail: "받는 곳 상세주소",
          product_name: "상품명",
          variant_name: "품목명",
          product_code: "상품코드",
          auth_code: "정품인증코드",
          purchase_store: "구매처",
          purchase_year: "구매년도",
          symptom: "증상",
          detail: "신청 상세 내역",
          receipt_done: "접수완료일",
          center_in: "센터입고일",
          payment_waiting: "입금대기중",
          payment_done: "입금완료",
          as_done: "AS완료일",
          shipping_done: "출고완료일",
          done: "처리완료일",
          repair_amount: "수리금액",
          tracking_no: "운송장번호",
          memo: "메모",
        },
      },
      statusButtons: [
        { text: "접수 대기 중", value: "receipt_waiting" },
        { text: "접수 완료", value: "receipt_done" },
        { text: "센터 입고", value: "center_in" },
        { text: "입금 대기 중", value: "payment_waiting" },
        { text: "입금 완료", value: "payment_done" },
        { text: "A/S 완료", value: "as_done" },
        { text: "출고 완료", value: "shipping_done" },
        { text: "처리 완료", value: "done" },
      ],
    };
  },
  components: {
    AuthCodeClickModal,
    AsRemarkModal,
    AsSymptomSettingModal,
    AsTrackingExcelUploadModal,
  },
  mixins: [CommonMixin],
  filters: Filters,
  watch: {
    options: {
      handler() {
        this.$parent.$refs.searchComponent.execSearch(false);
      },
      deep: true,
    },
  },
  methods: {
    setAsList(data) {
      const list = (data && (data.as_list || data.items)) || [];
      this.asListCount = data && typeof data.count !== "undefined" ? data.count : list.length;
      this.asList = list.map((item) => {
        if (item.product && item.product.product_name) {
          item.product.product_name = item.product.product_name.replace(/<\/?[^>]+(>|$)/g, "");
        }
        return item;
      });
      this.tableLoading = false;
    },
    getProductValue(item, key) {
      if (!item || !item.product || !item.product[key]) {
        return "-";
      }
      return item.product[key];
    },
    getAuthItemValue(item, label) {
      if (!item || !item.auth_items || !Array.isArray(item.auth_items)) {
        return "-";
      }
      const found = item.auth_items.find((authItem) => authItem.label === label);
      return found && found.value ? found.value : "-";
    },
    getRequestValue(item, key) {
      if (!item || !item.request || !item.request[key]) {
        return "-";
      }
      return item.request[key];
    },
    getRemarkValue(item, key) {
      if (!item || !item.remark || !item.remark[key]) {
        return "-";
      }
      return item.remark[key];
    },
    getRemarkRaw(item, key) {
      if (!item || !item.remark || !item.remark[key]) {
        return "";
      }
      return item.remark[key];
    },
    getStatusDate(item, key) {
      if (!item || !item.status_dates || !item.status_dates[key]) {
        return "-";
      }
      return item.status_dates[key];
    },
    hasAttachments(item) {
      return !!(item && item.request && item.request.attachments && item.request.attachments.length);
    },
    getSenderAddress(item) {
      if (!item || !item.request) {
        return "-";
      }
      const { sender_zip, sender_addr, sender_addr_detail } = item.request;
      if (!sender_zip && !sender_addr && !sender_addr_detail) {
        return "-";
      }
      const parts = [];
      if (sender_zip) parts.push(`[${sender_zip}]`);
      if (sender_addr) parts.push(sender_addr);
      if (sender_addr_detail) parts.push(sender_addr_detail);
      return parts.join(" ");
    },
    getReceiverAddress(item) {
      if (!item || !item.request) {
        return "-";
      }
      const { receiver_zip, receiver_addr, receiver_addr_detail } = item.request;
      if (!receiver_zip && !receiver_addr && !receiver_addr_detail) {
        return "-";
      }
      const parts = [];
      if (receiver_zip) parts.push(`[${receiver_zip}]`);
      if (receiver_addr) parts.push(receiver_addr);
      if (receiver_addr_detail) parts.push(receiver_addr_detail);
      return parts.join(" ");
    },
    statusText(value) {
      const map = {
        receipt_waiting: "접수 대기 중",
        receipt_done: "접수 완료",
        center_in: "센터 입고",
        payment_waiting: "입금 대기 중",
        payment_done: "입금 완료",
        as_done: "A/S 완료",
        shipping_done: "출고 완료",
        done: "처리 완료",
      };
      return map[value] || "-";
    },
    formatAmount(value) {
      if (value === null || value === undefined || value === "") {
        return "-";
      }
      return this.$options.filters.numberFormat(value);
    },
    showMemberInfo(userId) {
      const url = this.moveEcUserPage(userId);
      window.open(url, "_blank");
    },
    openAuthCodeHistory(item) {
      const authCode = item && item.product && item.product.auth_code;
      if (!authCode) {
        return;
      }
      this.$refs.AuthCodeClickModal.authCodeClick({
        serial_number: authCode,
      });
    },
    openAttachment(file) {
      const url = (file && file.url) || file;
      if (url) {
        window.open(url, "_blank");
      }
    },
    openTracking(trackingNo) {
      if (!trackingNo) {
        return;
      }
      const url = `https://search.naver.com/search.naver?where=nexearch&sm=top_hty&fbm=0&ie=utf8&query=${encodeURIComponent(trackingNo)}`;
      window.open(url, "_blank");
    },
    openRemarkModal(item) {
      this.$refs.AsRemarkModal.show(item);
    },
    setRemark(receiptNo, remark) {
      const target = this.asList.find((item) => item.receipt_no === receiptNo);
      if (target) {
        target.remark = remark;
      }
    },
    confirmDelete() {
      if (!this.selectedAsList.length) {
        this.alertShow("삭제하실 내역을 선택해 주세요.");
        return;
      }
      const ok = confirm("선택하신 AS 신청 내역을 정말 삭제하시겠습니까?");
      if (!ok) {
        return;
      }
      this.deleteSelectedItems();
    },
    confirmStatusChange(button) {
      if (!this.selectedAsList.length) {
        this.alertShow("내역을 선택해 주세요.");
        return;
      }
      const ok = confirm(`선택하신 AS 신청 내역의 상태를 [${button.text}] 상태로 변경하시겠습니까?`);
      if (!ok) {
        return;
      }
      this.updateSelectedStatus(button.value);
    },
    openSymptomSetting() {
      this.$refs.AsSymptomSettingModal.open();
    },
    openTrackingUpload() {
      this.$refs.AsTrackingExcelUploadModal.open();
    },
    getSelectedReceiptNos() {
      return this.selectedAsList.map((item) => item.receipt_no).filter(Boolean);
    },
    async deleteSelectedItems() {
      const receiptNos = this.getSelectedReceiptNos();
      if (!receiptNos.length) {
        this.alertShow("삭제하실 내역을 선택해 주세요.");
        return;
      }
      try {
        await Promise.all(receiptNos.map((receiptNo) => M.api(M.entry("/as/status", receiptNo), { status: "deleted" })));
        this.selectedAsList = [];
        this.$parent.$refs.searchComponent.execSearch(false);
      } catch (e) {
        this.alertShow("A/S 내역 삭제에 실패했습니다. 잠시 후 다시 시도해주세요.");
      }
    },
    async updateSelectedStatus(status) {
      const receiptNos = this.getSelectedReceiptNos();
      if (!receiptNos.length) {
        this.alertShow("내역을 선택해 주세요.");
        return;
      }
      try {
        console.log("receiptNos", receiptNos);
        await Promise.all(receiptNos.map((receiptNo) => M.api(M.entry("/as/status", receiptNo), { status })));
        this.selectedAsList = [];
        this.$parent.$refs.searchComponent.execSearch(false);
      } catch (e) {
        this.alertShow("A/S 상태 변경에 실패했습니다. 잠시 후 다시 시도해주세요.");
      }
    },
    excelDownload: async function() {
      this.excelData.totalCount = 0;
      this.excelData.offset = 0;
      let excelPayload = this.$parent.$refs.searchComponent.$data.searchPayload;
      excelPayload["limit"] = this.excelData.limit;
      excelPayload["offset"] = 0;

      let data = await M.api(M.entry("/as/excel"), excelPayload);
      const list = (data && (data.as_list || data.items)) || [];
      if (!list.length) {
        this.alertShow("엑셀다운로드할 데이터가 없습니다.");
        return false;
      }
      this.excelData.totalCount = data.count || list.length;
      if (this.excelData.totalCount > this.excelData.totalLimit) {
        this.alertShow(
          "엑셀다운로드건수는 " +
            this.$options.filters.numberFormat(this.excelData.totalLimit) +
            "건</br>이내로 검색하여 다운로드 해주세요.",
        );
        return false;
      }

      this.excelData.sheetData = list.map((item) => this.buildExcelRow(item));
      this.checkExcelDataChange();
      this.initExcelDownload();
    },
    initExcelDownload: function() {
      let progressText = "엑셀 다운로드중입니다.</br>잠시만 기다려 주세요.";
      this.circularProgressShow(progressText);
      this.excelData.Excel = new Excel(null, this.excelData.sheetHeader);
      this.addExcel();
    },
    getExcelDataAdd: async function() {
      this.excelData.offset = this.excelData.offset + this.excelData.limit;
      this.circularProgressSetValue(Math.round((this.excelData.offset / this.excelData.totalCount) * 100));
      if (this.excelData.offset > this.excelData.totalCount) {
        await this.endExcel();
        return false;
      }
      let excelPayload = this.$parent.$refs.searchComponent.$data.searchPayload;
      excelPayload["limit"] = this.excelData.limit;
      excelPayload["offset"] = this.excelData.offset;
      let data = await M.api(M.entry("/as/excel"), excelPayload);
      const list = (data && (data.as_list || data.items)) || [];
      if (!list.length) {
        await this.endExcel();
        return false;
      }

      this.excelData.sheetData = list.map((item) => this.buildExcelRow(item));
      this.checkExcelDataChange();
      this.excelData.Excel.addSheet(this.excelData.sheetData);
      this.addExcel();
    },
    addExcel: async function() {
      if (this.excelData.offset === 0) {
        await this.excelData.Excel.initSheet();
        await this.excelData.Excel.addSheet(this.excelData.sheetData);
        await this.getExcelDataAdd();
      } else {
        await this.getExcelDataAdd();
      }
    },
    endExcel: async function() {
      this.circularProgressHide();
      const toDay = new Date();
      const year = toDay.getFullYear();
      let month = 1 + toDay.getMonth();
      month = month >= 10 ? month : "0" + month;
      let day = toDay.getDate();
      day = day >= 10 ? day : "0" + day;
      await this.excelData.Excel.endSheet(`${year}${month}${day}_as_list`);
      this.excelData.Excel = null;
    },
    checkExcelDataChange: function() {
      if (this.excelData.sheetData.length > 0) {
        this.excelData.sheetData.forEach((data) => {
          if (data.product_name) {
            data.product_name = data.product_name.replace(/<[^>]*>/g, "");
          }
        });
      }
    },
    buildExcelRow(item) {
      const member = item && item.member;
      const product = item && item.product;
      const request = item && item.request;
      const statusDates = item && item.status_dates;
      const remark = item && item.remark;

      // Extract purchase_store and purchase_year from auth_items
      const getPurchaseValue = (label) => {
        if (!item || !item.auth_items || !Array.isArray(item.auth_items)) {
          return "";
        }
        const found = item.auth_items.find((authItem) => authItem.label === label);
        return found && found.value ? found.value : "";
      };

      return {
        receipt_no: item && item.receipt_no,
        received_at: item && item.received_at,
        status: this.statusText(item && item.status),
        member_id: member && member.id,
        member_name: member && member.name,
        member_phone: member && member.phone,
        member_email: member && member.email,
        member_level: member && member.level,
        sender_zip: request && request.sender_zip,
        sender_addr: request && request.sender_addr,
        sender_addr_detail: request && request.sender_addr_detail,
        receiver_zip: request && request.receiver_zip,
        receiver_addr: request && request.receiver_addr,
        receiver_addr_detail: request && request.receiver_addr_detail,
        product_name: product && product.product_name,
        variant_name: product && product.variant_name,
        product_code: product && product.product_code,
        auth_code: product && product.auth_code,
        purchase_store: getPurchaseValue("구매처"),
        purchase_year: getPurchaseValue("구매년도"),
        symptom: request && request.symptom,
        detail: request && request.detail,
        receipt_done: statusDates && statusDates.receipt_done,
        center_in: statusDates && statusDates.center_in,
        payment_waiting: statusDates && statusDates.payment_waiting,
        payment_done: statusDates && statusDates.payment_done,
        as_done: statusDates && statusDates.as_done,
        shipping_done: statusDates && statusDates.shipping_done,
        done: statusDates && statusDates.done,
        repair_amount: remark && remark.repair_amount,
        tracking_no: remark && remark.tracking_no,
        memo: remark && remark.memo,
      };
    },
  },
};
</script>

<style scoped>
.noDataProductInfos {
  padding: 50px 0;
}

.manageAsList a:hover {
  text-decoration: underline;
}

.memberInfoHover {
  cursor: pointer;
}
.v-data-table td,
.v-data-table th {
  padding: 0 10px;
}
</style>
